import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import app from "../../../firebaseConfig";
import GraficaTendenciaPorDia from "./graficatendenciapordia";
import GraficaCircularVentas from "./graficacircularventas";
import ProteccionAdmin from "./proteccionadmin";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

function GraficaProductos() {
  const [categorias, setCategorias] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("Todos");
  const [productosCategoria, setProductosCategoria] = useState([]);
  const [productosData, setProductosData] = useState({});
  const [mostrarLineaTendencia, setMostrarLineaTendencia] = useState(false);
  const [rangoTiempo, setRangoTiempo] = useState("Todo");

  // Helper: extraer fecha dd/mm/yyyy desde strings que pueden traer hora
  const extraerFechaDDMMYYYY = (fechaStr) => {
    if (!fechaStr) return null;
    // Buscar patrón dd/mm/yyyy
    const regex = /(\d{1,2}\/\d{1,2}\/\d{4})/;
    const match = fechaStr.match(regex);
    if (match) return match[1];
    // Si no, intentar crear Date directo y formatear
    const d = new Date(fechaStr);
    if (!isNaN(d)) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
    return null;
  };

  const cumpleRango = (fechaDDMMYYYY) => {
    if (!fechaDDMMYYYY) return false;
    const [dia, mes, año] = fechaDDMMYYYY.split("/").map(Number);
    const fechaPedido = new Date(año, mes - 1, dia);
    const ahora = new Date();
    const diasDiferencia = (ahora - fechaPedido) / (1000 * 60 * 60 * 24);

    switch (rangoTiempo) {
      case "Hoy": {
        const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate()).getTime();
        return fechaPedido.getTime() === hoy;
      }
      case "Ayer": {
        const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
        const ayer = new Date(hoy);
        ayer.setDate(hoy.getDate() - 1);
        return fechaPedido.getTime() === ayer.getTime();
      }
      case "1 semana":
        return diasDiferencia <= 7;
      case "2 semanas":
        return diasDiferencia <= 14;
      case "1 mes":
        return diasDiferencia <= 30;
      case "6 meses":
        return diasDiferencia <= 180;
      case "1 año":
        return diasDiferencia <= 365;
      default:
        return true;
    }
  };

  // Cargar categorías y productos vendidos (desde OrdenesPagadas)
  useEffect(() => {
    const db = getDatabase(app);
    const categoriasRef = ref(db, "nature/Awoo/Categorias");
    onValue(categoriasRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCategorias(["Todos", ...Object.keys(data)]);
      } else {
        setCategorias(["Todos"]);
      }
    });

    const ordenesPagadasRef = ref(db, "nature/Awoo/OrdenesPagadas");
    onValue(ordenesPagadasRef, (snapshot) => {
      const data = snapshot.val();
      let productosContador = {};

      if (data) {
        Object.values(data).forEach((pedido) => {
          // Soportar ambos formatos:
          // 1) viejo: pedido.productos = { key: { titulo, cantidad, ... }, ... }
          // 2) nuevo: pedido.producto (string) y pedido.cantidad (number)
          // 3) posible variante: pedido.producto = { titulo: "X", cantidad: N }
          // Extraer fecha y filtrar por rango de tiempo
          const fechaRaw = pedido.fecha || "";
          const fecha = extraerFechaDDMMYYYY(String(fechaRaw));
          if (!cumpleRango(fecha)) return;

          // Caso 1: productos como objeto
          if (pedido.productos && typeof pedido.productos === "object") {
            Object.values(pedido.productos).forEach((prod) => {
              const titulo = prod.titulo || prod.producto || "Sin título";
              const cantidad = Number(prod.cantidad) || 0;
              productosContador[titulo] = (productosContador[titulo] || 0) + cantidad;
            });
            return;
          }

          // Caso 2: producto individual simple
          if (pedido.producto) {
            // si producto es objeto con título
            if (typeof pedido.producto === "object") {
              const titulo = pedido.producto.titulo || pedido.producto.nombre || "Sin título";
              const cantidad = Number(pedido.cantidad) || Number(pedido.producto.cantidad) || 0;
              productosContador[titulo] = (productosContador[titulo] || 0) + cantidad;
            } else {
              const titulo = String(pedido.producto);
              const cantidad = Number(pedido.cantidad) || 0;
              productosContador[titulo] = (productosContador[titulo] || 0) + cantidad;
            }
            return;
          }

          // Caso 3: por si hay estructura diferente (ej: productoTitulo en la raíz)
          if (pedido.titulo || pedido.nombreProducto) {
            const titulo = pedido.titulo || pedido.nombreProducto;
            const cantidad = Number(pedido.cantidad) || 0;
            productosContador[titulo] = (productosContador[titulo] || 0) + cantidad;
            return;
          }

          // si no hay info reconocible, ignorar
        });
      }

      setProductosData(productosContador);
    });
  }, [rangoTiempo]);

  // Cuando seleccionan una categoría, obtenemos sus productos desde Categorias
  useEffect(() => {
    if (categoriaSeleccionada === "Todos") {
      setProductosCategoria(Object.keys(productosData));
      return;
    }

    if (!categoriaSeleccionada) {
      setProductosCategoria([]);
      return;
    }

    const db = getDatabase(app);
    const categoriaRef = ref(db, `nature/Awoo/Categorias/${categoriaSeleccionada}`);
    onValue(categoriaRef, (snapshot) => {
      const data = snapshot.val();
      setProductosCategoria(data ? Object.keys(data) : []);
    });
  }, [categoriaSeleccionada, productosData]);

  // Datos para la gráfica
  const datosGrafica = () => {
    // Si no hay productos en la categoría (o "Todos"), fallback a keys de productosData
    const productosFiltrados = (productosCategoria.length ? productosCategoria : Object.keys(productosData))
      .filter((titulo) => productosData[titulo] !== undefined && productosData[titulo] > 0);

    const cantidades = productosFiltrados.map((titulo) => productosData[titulo] || 0);
    const totalCompras = cantidades.reduce((acc, val) => acc + val, 0) || 1; // evitar division por 0
    const porcentajes = cantidades.map((cantidad) => ((cantidad / totalCompras) * 100).toFixed(2));

    const datasets = [
      {
        label: "Cantidad comprada",
        data: cantidades,
        backgroundColor: productosFiltrados.map(() => `hsl(${Math.random() * 360}, 70%, 50%)`),
        borderColor: "#fff",
        borderWidth: 1,
        yAxisID: "cantidad",
      }
    ];

    if (mostrarLineaTendencia) {
      datasets.push({
        label: "Tendencia de ventas",
        data: cantidades,
        type: "line",
        borderColor: "#FFD700",
        borderWidth: 3,
        pointBackgroundColor: "#FFD700",
        pointRadius: 4,
        yAxisID: "cantidad",
        order: 1,
      });
    }

    return {
      labels: productosFiltrados,
      datasets,
    };
  };

  const opcionesGrafica = {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const cantidad = context.raw;
            const total = Object.values(productosData).reduce((a, b) => a + b, 0) || 1;
            const porcentaje = ((cantidad / total) * 100).toFixed(2);
            return `${cantidad} unidades (${porcentaje}%)`;
          }
        }
      },
      legend: {
        labels: { color: "#fff" }
      }
    },
    scales: {
      cantidad: {
        type: "linear",
        position: "left",
        title: {
          display: true,
          text: "Cantidad Comprada",
          color: "#fff"
        },
        ticks: { color: "#fff" },
        grid: { color: "rgba(255, 255, 255, 0.2)" }
      },
      porcentaje: {
        type: "linear",
        position: "right",
        title: {
          display: true,
          text: "Porcentaje de Ventas",
          color: "#fff"
        },
        ticks: {
          color: "#fff",
          callback: (value) => `${value}%`
        },
        grid: { drawOnChartArea: false }
      }
    }
  };

  return (
    <ProteccionAdmin>
      <div style={{ padding: "20px", backgroundColor: "#111", color: "#fff", minHeight: "100vh" }}>
        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>📊 Rendimiento General</h2>
        <GraficaCircularVentas />

        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>📊 Productos más comprados</h2>

        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <label htmlFor="categoria" style={{ fontSize: "18px", marginRight: "10px" }}>Categoría:</label>
          <select
            id="categoria"
            onChange={(e) => setCategoriaSeleccionada(e.target.value)}
            value={categoriaSeleccionada}
            style={{ padding: "10px", fontSize: "16px", backgroundColor: "#222", color: "#fff", borderRadius: "5px" }}
          >
            {categorias.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <label htmlFor="tiempo" style={{ fontSize: "18px", marginRight: "10px" }}>Filtrar por tiempo:</label>
          <select
            id="tiempo"
            onChange={(e) => setRangoTiempo(e.target.value)}
            value={rangoTiempo}
            style={{ padding: "10px", fontSize: "16px", backgroundColor: "#222", color: "#fff", borderRadius: "5px" }}
          >
            {["Hoy", "Ayer", "Todo", "1 semana", "2 semanas", "1 mes", "6 meses", "1 año"].map((rango) => (
              <option key={rango} value={rango}>{rango}</option>
            ))}
          </select>
        </div>

        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <label>
            <input type="checkbox" checked={mostrarLineaTendencia} onChange={() => setMostrarLineaTendencia(!mostrarLineaTendencia)} />
            Mostrar línea de tendencia 📈
          </label>
        </div>

        <div style={{ maxWidth: "80%", margin: "0 auto" }}>
          <Bar data={datosGrafica()} options={opcionesGrafica} />
        </div>

        <div>
          <GraficaTendenciaPorDia />
        </div>
      </div>
    </ProteccionAdmin>
  );
}

export default GraficaProductos;
